import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'

import { AppShell } from './app/AppShell'
import {
  ActivityRoute,
  BillingRoute,
  CatalogRoute,
  RentStartRoute,
  ConfigureRoute,
  DocsRoute,
  InstanceDetailRoute,
  InstancesRoute,
  OffersRoute,
  OperatorRoute,
  OverviewRoute,
  DirectRentalGateway,
  RentalPathRoute,
  ReviewRoute,
  SdlRoute,
  SettingsRoute,
} from './app/routes'
import type { PaletteCommand } from './app/CommandPalette'
import { matchAppRoute } from './app/router'
import { useAccount, useChain, useIdentities, useLeases, useMe, useProviders } from './data/queries'
import { endpoints } from './data/endpoints'
import { leaseIdOf } from './data/adapters'
import { LoginPage } from './features/auth/LoginPage'
import { LegalPage } from './features/legal/LegalPage'
import { LegalFooter } from './ui/LegalFooter'
import { LandingPage } from './features/landing/LandingPage'
import { WalletBar } from './features/wallet/WalletBar'
import { available, connect, signCosmosLogin, type Connection, type WalletKind } from './core/wallet'

/**
 * One wallet sheet for the whole application surface.
 *
 * The workspace and the wallet bar both need to open it, and both can produce
 * the error that opens it by itself — so if each owned its own, a rejected
 * connection started from the Overview would raise a second, identical dialog
 * behind the first. The host owns the state instead, and both call up to it.
 * Each component keeps a local fallback for rendering on its own in tests.
 */
const WalletRecoverySheet = lazy(() =>
  import('./features/wallet/WalletRecoverySheet').then((module) => ({ default: module.WalletRecoverySheet })),
)

const STORAGE_KEY = 'ddws.console.wallet'
// Which linked address was connected last. A session may own several cosmos
// identities and /api/me names only the first, so without this a refresh
// always restored the oldest one, whatever the wallet had active.
const STORAGE_ADDRESS_KEY = 'ddws.console.wallet.address'

function forgetWallet() {
  window.localStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(STORAGE_ADDRESS_KEY)
}

function rememberWallet(kind: WalletKind, address: string) {
  window.localStorage.setItem(STORAGE_KEY, kind)
  window.localStorage.setItem(STORAGE_ADDRESS_KEY, address)
}

function readLocationKey(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function pathnameFromLocationKey(key: string): string {
  return new URL(key, window.location.href).pathname
}

/**
 * The application host owns URL state, wallet state, and data wiring. Every
 * user-facing screen is now a route in the SPA; the old tabbed compatibility
 * surface was intentionally removed with the Jinja/static operator UI.
 */
export function App() {
  // Keep the complete browser location as state. A query or hash can change
  // the data a route reads without changing its pathname, so storing only the
  // pathname leaves the address bar ahead of the rendered screen.
  const [locationKey, setLocationKey] = useState(readLocationKey)
  const path = pathnameFromLocationKey(locationKey)
  const [connection, setConnection] = useState<Connection | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [walletError, setWalletError] = useState<unknown>(null)
  // One wallet request at a time. Keplr and Leap queue every enable/sign call
  // as its own popup, and the server keeps a single cosmos challenge per
  // session, so a second flow started while the first is open both stacks a
  // prompt and invalidates the first flow's challenge. The controller also
  // lets the user stop waiting for an extension that will never answer.
  const walletRequest = useRef<AbortController | null>(null)
  // The remembered wallet is restored once per page load, from the session and
  // never from the extension: see the restore effect below.
  const restoreAttempted = useRef(false)
  const wallets = available()
  const route = matchAppRoute(path)
  // The legal pages are public and static, like the landing page: no session,
  // and no request of any kind.
  const legalPage = route?.id === 'privacy' || route?.id === 'terms'
  const appSurface = Boolean(route && route.id !== 'landing' && route.id !== 'login' && !legalPage)

  // The login route needs the public chain descriptor so Keplr/Leap can sign
  // the wallet challenge before a session exists. Other chain data remains
  // gated to the authenticated application surface.
  const chainQuery = useChain(Boolean(route && route.id !== 'landing' && !legalPage))
  const providersQuery = useProviders(appSurface)
  const meQuery = useMe(appSurface)
  const identitiesQuery = useIdentities(appSurface)
  const accountQuery = useAccount(connection?.address)
  const leasesQuery = useLeases(connection?.address)

  const chain = chainQuery.data ?? null
  const account = accountQuery.data ?? null
  const role = meQuery.data?.role

  const reloadAccount = useCallback(() => {
    void accountQuery.refetch()
    void leasesQuery.refetch()
  }, [accountQuery, leasesQuery])

  const doConnect = useCallback(
    async (kind: WalletKind) => {
      if (!chain || walletRequest.current) return
      const request = new AbortController()
      walletRequest.current = request
      const { signal } = request
      setConnecting(true)
      setWalletError(null)
      try {
        const next = await connect(kind, chain, undefined, { signal })
        // A connected wallet may be a new optional Google-recovery link. Prove
        // ownership with the same one-use ADR-36 challenge used at sign-in;
        // never associate an address merely because a browser reported it.
        const linked = identitiesQuery.data?.identities.some(
          (identity) => identity.provider === 'cosmos' && identity.subject === next.address,
        )
        if (!linked) {
          const challenge = await endpoints.cosmosChallenge({ address: next.address, chain_id: chain.chain_id })
          const signed = await signCosmosLogin(kind, chain.chain_id, next.address, challenge.message, next.publicKey, undefined, { signal })
          await endpoints.cosmosVerify({
            address: next.address,
            chain_id: chain.chain_id,
            message: challenge.message,
            signature: signed.signature,
            pub_key: signed.pubKey,
            pub_key_type: signed.pubKeyType,
          })
        }
        setConnection(next)
        rememberWallet(kind, next.address)
        await identitiesQuery.refetch()
      } catch (error) {
        setWalletError(error)
      } finally {
        if (walletRequest.current === request) walletRequest.current = null
        setConnecting(false)
      }
    },
    [chain, identitiesQuery],
  )

  const doCosmosLogin = useCallback(async (kind: WalletKind) => {
    if (!chain) throw new Error('The console is still loading its chain configuration.')
    if (walletRequest.current) throw new Error('A wallet request is already open. Finish or close it in your wallet first.')
    const request = new AbortController()
    walletRequest.current = request
    const { signal } = request
    try {
      const next = await connect(kind, chain, undefined, { signal })
      const challenge = await endpoints.cosmosChallenge({ address: next.address, chain_id: chain.chain_id })
      const signed = await signCosmosLogin(kind, chain.chain_id, next.address, challenge.message, next.publicKey, undefined, { signal })
      const result = await endpoints.cosmosVerify({
        address: next.address,
        chain_id: chain.chain_id,
        message: challenge.message,
        signature: signed.signature,
        pub_key: signed.pubKey,
        pub_key_type: signed.pubKeyType,
      })
      setConnection(next)
      rememberWallet(kind, next.address)
      return result
    } finally {
      if (walletRequest.current === request) walletRequest.current = null
    }
  }, [chain])

  // Stop waiting for the open wallet call. Its flow unwinds at once through
  // its own `finally`, which is what frees the guard for the next attempt.
  const cancelWalletRequest = useCallback(() => walletRequest.current?.abort(), [])

  // The sign-in page is where a signed-out visitor lands, including straight
  // after /logout, which clears only the server session. Forgetting the wallet
  // here means the next account to sign in on this browser is not offered the
  // previous one's wallet; a wallet sign-in from this page remembers it again.
  useEffect(() => {
    if (route?.id === 'login') forgetWallet()
  }, [route?.id])

  // Restore the remembered wallet from the server session, without touching the
  // extension. Calling enable() here opened Keplr's unlock window on every page
  // load whenever the wallet was locked — a prompt nobody asked for. The session
  // already knows the linked address, and every signing path asks the wallet
  // for its signer at the moment of the action, so an unlock prompt now only
  // ever answers a click. Only inside the workspace: the sign-in page forgets
  // the remembered wallet instead.
  //
  // The address last connected wins over the session's, but only while the
  // session still owns it: the browser's memory is a preference, the linked
  // identities are the authority.
  const sessionAddress = meQuery.data?.cosmos_address
  useEffect(() => {
    if (!appSurface || connection || !sessionAddress || identitiesQuery.isLoading || restoreAttempted.current) return
    const remembered = window.localStorage.getItem(STORAGE_KEY)
    if (remembered !== 'keplr' && remembered !== 'leap') return
    restoreAttempted.current = true
    const rememberedAddress = window.localStorage.getItem(STORAGE_ADDRESS_KEY)
    const owned = identitiesQuery.data?.identities.some(
      (identity) => identity.provider === 'cosmos' && identity.subject === rememberedAddress,
    )
    setConnection({ wallet: remembered, address: owned && rememberedAddress ? rememberedAddress : sessionAddress, name: '' })
  }, [appSurface, connection, sessionAddress, identitiesQuery.isLoading, identitiesQuery.data])

  const disconnect = useCallback(() => {
    forgetWallet()
    setConnection(null)
    setWalletError(null)
  }, [])

  const [walletSheetOpen, setWalletSheetOpen] = useState(false)
  // Mounted from the first open onward, so closing still returns focus to the
  // control that opened it rather than dropping focus on the document.
  const [walletSheetUsed, setWalletSheetUsed] = useState(false)
  const openWalletSheet = useCallback(() => {
    setWalletSheetUsed(true)
    setWalletSheetOpen(true)
  }, [])
  // A failure nobody opened the sheet to see is still a silent failure — this
  // covers the reconnect the host attempts on its own when a wallet is
  // remembered, which no user action would otherwise surface.
  useEffect(() => { if (walletError) { setWalletSheetUsed(true); setWalletSheetOpen(true) } }, [walletError])
  useEffect(() => { if (connection) setWalletSheetOpen(false) }, [connection])

  useEffect(() => {
    const onLocationChange = () => setLocationKey(readLocationKey())
    window.addEventListener('popstate', onLocationChange)
    window.addEventListener('hashchange', onLocationChange)
    return () => {
      window.removeEventListener('popstate', onLocationChange)
      window.removeEventListener('hashchange', onLocationChange)
    }
  }, [])

  const navigate = useCallback((next: string) => {
    const target = new URL(next, window.location.href)
    window.history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`)
    // Route matching is based on the pathname. Keep query/hash in the
    // location key so the destination route rerenders and rereads them.
    setLocationKey(`${target.pathname}${target.search}${target.hash}`)
  }, [])

  if (route?.id === 'landing') return <LandingPage onNavigate={navigate} />
  if (route?.id === 'privacy' || route?.id === 'terms') return <LegalPage document={route.id} />
  if (route?.id === 'login') return <LoginPage onSubmit={endpoints.login} onSignup={endpoints.signup} onWalletLogin={doCosmosLogin} onCancelWalletLogin={cancelWalletRequest} onGoogleLogin={() => window.location.assign('/auth/google/start')} wallets={wallets} />

  const offerDseq = route?.id === 'offers' ? route.params.dseq : undefined
  const instanceDseq = route?.id === 'instance' ? route.params.dseq : undefined
  const operatorSection = route?.id === 'ops-overview'
    ? 'overview'
    : route?.id === 'ops-hosts'
      ? 'hosts'
      : route?.id === 'ops-workloads'
        ? 'workloads'
        : route?.id === 'ops-failover'
          ? 'failover'
          : route?.id === 'ops-diagnostics'
            ? 'diagnostics'
            : route?.id === 'ops-logs'
              ? 'logs'
              : route?.id === 'ops-admin'
                ? 'admin'
                : route?.id === 'ops-versions'
                  ? 'versions'
                : null
  const operatorHostId = route?.id === 'ops-hosts' ? route.params.id : undefined
  // Always defined. A missing extension is a state the sheet explains, not a
  // reason to hand a feature a disabled button with nothing behind it.
  const connectWallet = openWalletSheet

  const content = operatorSection
    ? <OperatorRoute section={operatorSection} role={role} hostId={operatorHostId} onNavigate={navigate} />
    : route?.id === 'overview'
      ? <OverviewRoute account={account} walletConnected={Boolean(connection)} accountLoading={accountQuery.isLoading} accountError={accountQuery.error} onRetryAccount={() => void accountQuery.refetch()} onConnect={connectWallet} onOpenWallet={openWalletSheet} onNavigate={navigate} />
      : route?.id === 'rent'
        ? <RentStartRoute walletAddress={connection?.address} onNavigate={navigate} />
        : route?.id === 'rent-hosts'
          ? <CatalogRoute onNavigate={navigate} />
        : route?.id === 'rental-path'
          ? <RentalPathRoute chain={chain} onNavigate={navigate} />
        : route?.id === 'configure'
          ? <ConfigureRoute walletAddress={connection?.address} chain={chain} connection={connection} onNavigate={navigate} />
          : route?.id === 'review'
            ? <ReviewRoute account={account} chain={chain} connection={connection} onNavigate={navigate} />
          : route?.id === 'direct-rental'
            ? <DirectRentalGateway account={account} chain={chain} connection={connection} onNavigate={navigate} />
            : offerDseq
              ? <OffersRoute account={account} dseq={offerDseq} chain={chain} connection={connection} onNavigate={navigate} />
              : instanceDseq
                ? <InstanceDetailRoute account={account} dseq={instanceDseq} chain={chain} connection={connection} onNavigate={navigate} />
                : route?.id === 'instances'
                  ? <InstancesRoute account={account} onConnect={connectWallet} onNavigate={navigate} />
                  : route?.id === 'billing'
                  ? <BillingRoute account={account} walletConnected={Boolean(connection)} accountLoading={accountQuery.isLoading} accountError={accountQuery.error} onRetryAccount={() => void accountQuery.refetch()} onConnect={connectWallet} onNavigate={navigate} />
                    : route?.id === 'settings'
                      ? <SettingsRoute account={account} connection={connection} identities={identitiesQuery.data?.identities ?? []} onConnectGoogle={() => window.location.assign('/auth/google/start?mode=link')} />
                      : route?.id === 'sdl'
                        ? <SdlRoute chain={chain} connection={connection} onDeployed={reloadAccount} />
                        : route?.id === 'activity'
                          ? <ActivityRoute onNavigate={navigate} />
                          : route?.id === 'docs' || route?.id === 'guide'
                            ? <DocsRoute slug={route.id === 'guide' ? route.params.slug : undefined} onNavigate={navigate} />
                          : <p className="empty">That console page does not exist.</p>

  const paletteCommands: PaletteCommand[] = [
    ...(leasesQuery.data?.leases ?? []).flatMap((item, index) => {
      const dseq = leaseIdOf(item)?.dseq
      return dseq
        ? [{ label: `Your instance${leasesQuery.data!.leases.length > 1 ? ` ${index + 1}` : ''}`, path: `/app/instances/${encodeURIComponent(dseq)}` }]
        : []
    }),
    ...(providersQuery.data?.providers ?? []).map((provider) => ({
      label: `Host ${provider.organization || provider.owner}`,
      path: `/app/rent/configure?host=${encodeURIComponent(provider.owner)}&path=host-available`,
    })),
  ]

  return (
    <AppShell
      path={path}
      role={role}
      commands={paletteCommands}
      onNavigate={navigate}
      topbar={<WalletBar chain={chain} chainError={chainQuery.error} walletError={walletError} connection={connection} account={account} wallets={wallets} connecting={connecting} role={role} onConnect={doConnect} onOpenWallet={openWalletSheet} onDisconnect={disconnect} onNavigate={navigate} />}
      footer={<footer className="footer"><span>Transactions are signed in your wallet — this console never holds your key.</span><LegalFooter /></footer>}
    >
      {content}
      {walletSheetUsed && (
        <Suspense fallback={null}>
          <WalletRecoverySheet
            open={walletSheetOpen}
            onOpenChange={setWalletSheetOpen}
            wallets={wallets}
            connecting={connecting}
            error={walletError}
            connected={Boolean(connection)}
            onConnect={(kind) => void doConnect(kind)}
            onCancel={cancelWalletRequest}
          />
        </Suspense>
      )}
    </AppShell>
  )
}
